/**
 * /app/compras — Recepciones (CRUD create/read) + alertas.
 * El target de recepción depende del nicho (stock-model):
 * - ingredient (gastro+BOM): insumos → rinde recetas
 * - sku (retail/moda/…): productos → stock SKU
 * - hybrid: productos + insumos opcionales
 * Gateado por supply_chain.
 */
import { InlineSupplierAi } from "@/components/renata/InlineSupplierAi";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, CheckCircle2, Plus, RefreshCw, X, PackagePlus } from "lucide-react";
import { DataTable, CrudEmptyState, Loading, Panel, QuickActions } from "@/components/ds";
import type { DataTableColumn, QuickAction } from "@/components/ds";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useWorkspaceLocationId } from "@/lib/location-context";
import { useTenantContextSafe } from "@/lib/tenant-context";
import {
  abastecimientoBannerCopy,
  resolveStockControlMode,
  supplyReceivesIngredients,
  type StockControlMode,
} from "@/lib/stock-model";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/compras/")({
  component: ComprasPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab:
      s.tab === "alertas" || s.tab === "recepciones"
        ? (s.tab as "alertas" | "recepciones")
        : "recepciones",
    ingredientId: typeof s.ingredientId === "string" && s.ingredientId ? s.ingredientId : undefined,
  }),
  head: () => ({ meta: [{ title: "Compras — Supply Chain" }] }),
});

type Unit = "GRAMOS" | "KILOS" | "MILILITROS" | "LITROS" | "UNIDADES";

interface StockReceipt {
  id: string;
  numero: string;
  fechaRecepcion: string;
  supplierId: string | null;
  purchaseOrderId: string | null;
  notas: string | null;
}
interface ReceiptLine {
  id: string;
  ingredientId: string | null;
  productId?: string | null;
  descripcion: string;
  qtyReceived: number;
  unit: string;
  costoUnitario: number;
  loteNumero?: string | null;
  venceAt?: string | null;
}
interface ProductOpt {
  id: string;
  name: string;
  stockActual: number | null;
}
interface Alert {
  id: string;
  type: string;
  status: string;
  entityId: string | null;
  entityType: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}
interface Supplier {
  id: string;
  nombre: string;
}
interface Location {
  id: string;
  name: string;
  active: boolean;
}
interface Ingredient {
  id: string;
  nombre: string;
  unidadBase: Unit;
  stockActual: number;
  costoUnitario: number;
}
interface PurchaseOrder {
  id: string;
  numero: string | null;
  estado: string;
}

type ReceiptItemKind = "ingredient" | "product";

interface ProductPresentationOpt {
  id: string;
  name: string;
  unitFactor: number;
}

type DraftLine = {
  kind: ReceiptItemKind;
  ingredientId: string;
  productId: string;
  /** SCRUM-91: presentación en la que se recibe (ej: "Caja x24"). "" = unidad base. */
  presentationId: string;
  qty: string;
  costoUnitario: string;
  loteNumero: string;
  venceAt: string;
};

const ALERT_TYPE_LABELS: Record<string, string> = {
  low_stock: "Stock bajo",
  low_margin: "Margen bajo",
  batch_expiry: "Vencimiento próximo",
  po_overdue: "OC vencida",
};

const UNIT_SHORT: Record<string, string> = {
  GRAMOS: "g",
  KILOS: "kg",
  MILILITROS: "ml",
  LITROS: "L",
  UNIDADES: "u",
};

function nextReceiptNumero() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const r = String(Math.floor(Math.random() * 900) + 100);
  return `RCP-${y}${m}${day}-${r}`;
}

function emptyLine(kind: ReceiptItemKind, id = ""): DraftLine {
  return {
    kind,
    ingredientId: kind === "ingredient" ? id : "",
    productId: kind === "product" ? id : "",
    presentationId: "",
    qty: "1",
    costoUnitario: "",
    loteNumero: "",
    venceAt: "",
  };
}

function defaultReceiptKind(mode: StockControlMode): ReceiptItemKind {
  return supplyReceivesIngredients(mode) ? "ingredient" : "product";
}

function ComprasPage() {
  const tid = useWorkspaceTenantId();
  const contextLocationId = useWorkspaceLocationId();
  const navigate = useNavigate({ from: "/app/compras" });
  const { tab, ingredientId: prefillIngredientId } = Route.useSearch();
  const tenantCtx = useTenantContextSafe();
  const stockMode = resolveStockControlMode(tenantCtx?.businessProfile, {
    inventoryBom: tenantCtx?.productFlags?.inventory_bom === true,
  });
  const banner = abastecimientoBannerCopy(stockMode);
  const [receiptKind, setReceiptKind] = useState<ReceiptItemKind>(() =>
    prefillIngredientId ? "ingredient" : defaultReceiptKind(stockMode),
  );

  const [receipts, setReceipts] = useState<StockReceipt[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);

  const [showForm, setShowForm] = useState(!!prefillIngredientId);
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailLines, setDetailLines] = useState<ReceiptLine[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [form, setForm] = useState({
    numero: nextReceiptNumero(),
    supplierId: "",
    purchaseOrderId: "",
    notas: "",
    locationId: "",
  });
  const [lines, setLines] = useState<DraftLine[]>([
    emptyLine(
      prefillIngredientId ? "ingredient" : defaultReceiptKind(stockMode),
      prefillIngredientId ?? "",
    ),
  ]);
  const [ingFilter, setIngFilter] = useState("");
  // SCRUM-91: presentaciones (six pack, caja x24...) por producto, cargadas
  // on-demand cuando se elige un producto en una línea de recepción.
  const [presentationsByProduct, setPresentationsByProduct] = useState<Record<string, ProductPresentationOpt[]>>({});

  const loadPresentationsForProduct = async (productId: string) => {
    if (!tid || !productId || presentationsByProduct[productId]) return;
    try {
      const list = await apiGetJson<ProductPresentationOpt[]>(
        `/api/products/presentations?productId=${encodeURIComponent(productId)}`,
        tid,
      );
      setPresentationsByProduct((prev) => ({ ...prev, [productId]: list }));
    } catch {
      setPresentationsByProduct((prev) => ({ ...prev, [productId]: [] }));
    }
  };

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [rr, ar, sr, ir, or, pr, lr] = await Promise.all([
        apiGetJson<{ receipts?: StockReceipt[]; _lock?: PlanApiErrorPayload }>(
          `/api/supply-chain?resource=receipts`,
          tid,
        ),
        apiGetJson<{ alerts?: Alert[] }>(`/api/supply-chain?resource=alerts`, tid),
        apiGetJson<{ suppliers?: Supplier[] }>(`/api/supply-chain?resource=suppliers`, tid),
        apiGetJson<{ ingredients?: Ingredient[] }>(`/api/ingredients`, tid).catch(() => ({
          ingredients: [] as Ingredient[],
        })),
        apiGetJson<{ orders?: PurchaseOrder[] }>(`/api/supply-chain?resource=purchase-orders`, tid),
        apiGetJson<{ items: ProductOpt[] } | ProductOpt[]>(`/api/products`, tid).catch(
          () => [] as ProductOpt[],
        ),
        apiGetJson<Location[]>(`/api/locations`, tid).catch(() => []),
      ]);
      if (rr._lock) {
        setLock(rr._lock);
        return;
      }
      setReceipts(rr.receipts ?? []);
      setAlerts(ar.alerts ?? []);
      setSuppliers(sr.suppliers ?? []);
      setIngredients(ir.ingredients ?? []);
      setOrders(or.orders ?? []);
      setProducts(Array.isArray(pr) ? pr : pr.items ?? []);
      setLocations(lr.filter((l) => l.active));
    } catch {
      toast.error("Error al cargar compras");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [tid]);

  // SCRUM-72: default desde el contexto único de sucursal activa.
  useEffect(() => {
    if (contextLocationId && !form.locationId) {
      setForm((f) => ({ ...f, locationId: contextLocationId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextLocationId]);

  useEffect(() => {
    if (!prefillIngredientId) return;
    setReceiptKind("ingredient");
    setShowForm(true);
    setLines((prev) => {
      if (prev.some((l) => l.kind === "ingredient" && l.ingredientId === prefillIngredientId))
        return prev;
      return [
        emptyLine("ingredient", prefillIngredientId),
        ...prev.filter((l) => l.kind !== "ingredient" || l.ingredientId),
      ];
    });
  }, [prefillIngredientId]);

  useEffect(() => {
    if (prefillIngredientId || showForm) return;
    const next = defaultReceiptKind(stockMode);
    setReceiptKind(next);
    setLines([emptyLine(next)]);
  }, [stockMode, prefillIngredientId, showForm]);

  const ingById = useMemo(() => {
    const m = new Map<string, Ingredient>();
    for (const i of ingredients) m.set(i.id, i);
    return m;
  }, [ingredients]);

  const prodById = useMemo(() => {
    const m = new Map<string, ProductOpt>();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  const filteredIngredients = useMemo(() => {
    const q = ingFilter.trim().toLowerCase();
    if (!q) return ingredients;
    return ingredients.filter((i) => i.nombre.toLowerCase().includes(q));
  }, [ingredients, ingFilter]);

  const filteredProducts = useMemo(() => {
    const q = ingFilter.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, ingFilter]);

  // El toggle de arriba solo fija el tipo por default de la PRÓXIMA línea que
  // se agregue — no pisa las líneas ya cargadas. Una recepción en modo híbrido
  // puede mezclar productos e insumos (ej: cerveza + tomate en el mismo remito);
  // cada línea guarda su propio `kind` (ver DraftLine) y el backend
  // (dbSmCreateReceipt) ya procesa cada línea de forma independiente.
  const switchReceiptKind = (kind: ReceiptItemKind) => {
    setReceiptKind(kind);
    setIngFilter("");
  };

  const openDetail = async (receiptId: string) => {
    if (!tid) return;
    setDetailId(receiptId);
    setDetailLoading(true);
    try {
      const r = await apiGetJson<{ lines?: ReceiptLine[] }>(
        `/api/supply-chain?resource=receipts&receiptId=${encodeURIComponent(receiptId)}`,
        tid,
      );
      setDetailLines(r.lines ?? []);
    } catch {
      toast.error("No se pudieron cargar las líneas");
      setDetailLines([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAck = async (alertId: string) => {
    if (!tid) return;
    await apiSendJson(`/api/supply-chain`, "POST", { action: "ack-alert", alertId }, tid);
    toast.success("Alerta reconocida");
    void load();
  };

  const addLine = () => setLines((l) => [...l, emptyLine(receiptKind)]);
  const removeLine = (i: number) => setLines((l) => l.filter((_, idx) => idx !== i));
  const updateLine = (i: number, patch: Partial<DraftLine>) => {
    setLines((l) =>
      l.map((line, idx) => {
        if (idx !== i) return line;
        let next = { ...line, ...patch };
        if (patch.kind && patch.kind !== line.kind) {
          // Cambiar el tipo de una línea individual limpia la selección del
          // otro tipo — evita mandar ingredientId y productId juntos.
          next = { ...next, ingredientId: "", productId: "" };
        }
        if (patch.ingredientId) {
          const ing = ingById.get(patch.ingredientId);
          if (ing && !line.costoUnitario) {
            next.costoUnitario = String(ing.costoUnitario || "");
          }
        }
        if (patch.productId !== undefined && patch.productId !== line.productId) {
          // Cambiar de producto invalida la presentación elegida antes.
          next.presentationId = "";
          if (patch.productId) void loadPresentationsForProduct(patch.productId);
        }
        return next;
      }),
    );
  };

  const handleCreate = async () => {
    if (!tid) return;
    const valid = lines.filter((l) => {
      const id = l.kind === "ingredient" ? l.ingredientId : l.productId;
      return !!id && parseFloat(l.qty) > 0;
    });
    if (!valid.length) {
      toast.error("Agregá al menos un ítem con cantidad");
      return;
    }
    if (!form.numero.trim()) {
      toast.error("Número de recepción requerido");
      return;
    }
    setSaving(true);
    try {
      await apiSendJson(
        `/api/supply-chain`,
        "POST",
        {
          action: "receipt",
          numero: form.numero.trim(),
          supplierId: form.supplierId || null,
          purchaseOrderId: form.purchaseOrderId || null,
          notas: form.notas.trim() || null,
          locationId: form.locationId || null,
          lines: valid.map((l) => {
            if (l.kind === "ingredient") {
              const ing = ingById.get(l.ingredientId);
              return {
                ingredientId: l.ingredientId,
                productId: null,
                descripcion: ing?.nombre ?? l.ingredientId,
                qtyReceived: parseFloat(l.qty),
                unit: ing?.unidadBase ?? "UNIDADES",
                costoUnitario: parseFloat(l.costoUnitario) || 0,
                loteNumero: l.loteNumero.trim() || null,
                venceAt: l.venceAt || null,
              };
            }
            const prod = prodById.get(l.productId);
            return {
              ingredientId: null,
              productId: l.productId,
              presentationId: l.presentationId || null,
              descripcion: prod?.name ?? l.productId,
              qtyReceived: parseFloat(l.qty),
              unit: "UNIDADES" as Unit,
              costoUnitario: parseFloat(l.costoUnitario) || 0,
              loteNumero: l.loteNumero.trim() || null,
              venceAt: l.venceAt || null,
            };
          }),
        },
        tid,
      );
      const validHasIngredient = valid.some((l) => l.kind === "ingredient");
      const validHasProduct = valid.some((l) => l.kind === "product");
      toast.success(
        validHasIngredient && validHasProduct
          ? "Recepción registrada — stock de insumos y productos actualizado"
          : validHasIngredient
          ? "Recepción registrada — stock de insumos actualizado"
          : "Recepción registrada — stock de productos actualizado",
      );
      setShowForm(false);
      setForm({ numero: nextReceiptNumero(), supplierId: "", purchaseOrderId: "", notas: "", locationId: "" });
      setLines([emptyLine(receiptKind)]);
      void load();
    } catch {
      toast.error("Error al registrar la recepción");
    } finally {
      setSaving(false);
    }
  };

  const columns: DataTableColumn<StockReceipt>[] = [
    {
      key: "numero",
      header: "Número",
      render: (r) => (
        <button
          type="button"
          className="font-medium tabular-nums text-primary underline-offset-2 hover:underline"
          onClick={() => void openDetail(r.id)}
        >
          {r.numero}
        </button>
      ),
    },
    {
      key: "fecha",
      header: "Fecha",
      render: (r) => new Date(r.fechaRecepcion).toLocaleDateString("es-AR"),
    },
    {
      key: "proveedor",
      header: "Proveedor",
      render: (r) => {
        const s = suppliers.find((x) => x.id === r.supplierId);
        return <span className="text-xs text-muted-foreground">{s?.nombre ?? "—"}</span>;
      },
    },
    {
      key: "oc",
      header: "OC",
      render: (r) => {
        const o = orders.find((x) => x.id === r.purchaseOrderId);
        return <span className="text-xs">{o?.numero ?? r.purchaseOrderId ?? "—"}</span>;
      },
    },
    {
      key: "notas",
      header: "Notas",
      render: (r) => <span className="text-xs text-muted-foreground">{r.notas ?? "—"}</span>,
    },
  ];

  const headerActions: QuickAction[] = [
    {
      key: "nueva",
      label: "Nueva recepción",
      icon: Plus,
      onClick: () => {
        setShowForm((v) => !v);
        if (!showForm) setForm((f) => ({ ...f, numero: nextReceiptNumero() }));
      },
    },
    { key: "refresh", label: "Actualizar", icon: RefreshCw, variant: "ghost", onClick: () => void load() },
  ];

  if (loading) return <Loading variant="list" rows={3} />;
  if (lock) return <FeatureLockedState payload={lock} />;

  const detailReceipt = detailId ? receipts.find((r) => r.id === detailId) : null;

  const hasIngredientLine = lines.some((l) => l.kind === "ingredient");
  const hasProductLine = lines.some((l) => l.kind === "product");
  const isMixedReceipt = hasIngredientLine && hasProductLine;
  const receiptTitle = isMixedReceipt
    ? "Nueva recepción"
    : receiptKind === "ingredient"
    ? "Nueva recepción de insumos"
    : "Nueva recepción de productos";
  const receiptDescription = isMixedReceipt
    ? "Suma stock a los insumos y productos de esta recepción — cada línea actualiza su propio stock."
    : receiptKind === "ingredient"
    ? "Suma stock al insumo y actualiza el rinde de las recetas."
    : "Suma stock al SKU del producto. En moda, el desglose por talle/color se ajusta en el producto.";

  return (
    <div className="space-y-4">
      <MerchantExperienceBar screenId="compras" />
      <div className="flex justify-end">
        <QuickActions actions={headerActions} />
      </div>

      {tab === "recepciones" ? (
        <p className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          <PackagePlus className="mr-1.5 inline h-3.5 w-3.5" />
          <span className="font-medium text-foreground">{banner.title}.</span> {banner.body}{" "}
          {stockMode === "ingredient" ? (
            <>
              Ver{" "}
              <Link to="/app/ingredientes" className="underline underline-offset-2 hover:text-foreground">
                Insumos
              </Link>{" "}
              y{" "}
              <Link to="/app/productos" className="underline underline-offset-2 hover:text-foreground">
                recetas
              </Link>
              .
            </>
          ) : stockMode === "hybrid" ? (
            <>
              Podés recibir productos o{" "}
              <Link to="/app/ingredientes" className="underline underline-offset-2 hover:text-foreground">
                insumos
              </Link>
              .
            </>
          ) : null}
        </p>
      ) : null}

      {alerts.length > 0 && tab === "recepciones" ? (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            {alerts.length} alerta{alerts.length > 1 ? "s" : ""} activa{alerts.length > 1 ? "s" : ""}
          </span>
          <button
            type="button"
            onClick={() => void navigate({ search: { tab: "alertas" } })}
            className="ml-auto text-xs underline"
          >
            Ver alertas
          </button>
        </div>
      ) : null}

      {tab === "recepciones" && showForm ? (
        <Panel
          title={receiptTitle}
          description={receiptDescription}
          actions={
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          }
        >
          <div className="space-y-4">
            {stockMode === "hybrid" ? (
              <div className="space-y-1">
                <p className="text-[11px] text-muted-foreground">
                  Tipo por defecto para las líneas nuevas — podés mezclar productos e insumos en la misma recepción, cada línea tiene su propio tipo abajo.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={receiptKind === "product" ? "default" : "outline"}
                    onClick={() => switchReceiptKind("product")}
                  >
                    Productos
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={receiptKind === "ingredient" ? "default" : "outline"}
                    onClick={() => switchReceiptKind("ingredient")}
                  >
                    Insumos
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <div className="min-w-40">
                <label className="text-xs text-muted-foreground">Número</label>
                <Input
                  value={form.numero}
                  onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))}
                  className="mt-1 h-9"
                />
              </div>
              <div className="min-w-48 flex-1">
                <label className="text-xs text-muted-foreground">Proveedor (opcional)</label>
                <select
                  value={form.supplierId}
                  onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))}
                  className="mt-1 block h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                >
                  <option value="">Sin proveedor</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-48 flex-1">
                <label className="text-xs text-muted-foreground">OC asociada (opcional)</label>
                <select
                  value={form.purchaseOrderId}
                  onChange={(e) => setForm((f) => ({ ...f, purchaseOrderId: e.target.value }))}
                  className="mt-1 block h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                >
                  <option value="">Sin OC</option>
                  {orders.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.numero ?? o.id} · {o.estado}
                    </option>
                  ))}
                </select>
              </div>
              {locations.length >= 2 && (
                <div className="min-w-48 flex-1">
                  <label className="text-xs text-muted-foreground">Sucursal que recibe</label>
                  <select
                    value={form.locationId}
                    onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
                    className="mt-1 block h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                  >
                    <option value="">Default del comercio</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Notas (opcional)</label>
              <Input
                value={form.notas}
                onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
                className="mt-1 h-9"
                placeholder="Remito, chofer, observaciones…"
              />
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {isMixedReceipt ? "Ítems recibidos" : hasIngredientLine ? "Insumos recibidos" : "Productos recibidos"}
                </p>
                <Input
                  value={ingFilter}
                  onChange={(e) => setIngFilter(e.target.value)}
                  placeholder="Filtrar…"
                  className="h-8 max-w-xs text-xs"
                />
              </div>

              {lines.map((line, i) => {
                const ing = ingById.get(line.ingredientId);
                return (
                  <div
                    key={i}
                    className="space-y-1.5 rounded-lg border border-border/70 bg-background/50 p-2"
                  >
                    {stockMode === "hybrid" ? (
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant={line.kind === "product" ? "secondary" : "ghost"}
                          className="h-6 px-2 text-[11px]"
                          onClick={() => updateLine(i, { kind: "product" })}
                        >
                          Producto
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={line.kind === "ingredient" ? "secondary" : "ghost"}
                          className="h-6 px-2 text-[11px]"
                          onClick={() => updateLine(i, { kind: "ingredient" })}
                        >
                          Insumo
                        </Button>
                      </div>
                    ) : null}
                    <div className="grid gap-2 sm:grid-cols-[1.4fr_0.6fr_0.7fr_0.7fr_0.8fr_auto]">
                    <div>
                      <label className="text-[11px] text-muted-foreground">
                        {line.kind === "ingredient" ? "Ingrediente" : "Producto"}
                      </label>
                      {line.kind === "ingredient" ? (
                        <select
                          value={line.ingredientId}
                          onChange={(e) => updateLine(i, { ingredientId: e.target.value })}
                          className="mt-0.5 block h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                        >
                          <option value="">Seleccionar…</option>
                          {filteredIngredients.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.nombre} ({UNIT_SHORT[opt.unidadBase] ?? opt.unidadBase}) · stock{" "}
                              {opt.stockActual}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={line.productId}
                          onChange={(e) => updateLine(i, { productId: e.target.value })}
                          className="mt-0.5 block h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                        >
                          <option value="">Seleccionar…</option>
                          {filteredProducts.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                              {opt.stockActual != null ? ` · stock ${opt.stockActual}` : ""}
                            </option>
                          ))}
                        </select>
                      )}
                      {line.kind === "product" && line.productId && (presentationsByProduct[line.productId]?.length ?? 0) > 0 ? (
                        <select
                          value={line.presentationId}
                          onChange={(e) => updateLine(i, { presentationId: e.target.value })}
                          className="mt-1 block h-8 w-full rounded-md border border-border bg-background px-2 text-xs"
                        >
                          <option value="">Unidad base</option>
                          {presentationsByProduct[line.productId]!.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} (contiene {p.unitFactor})
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground">
                        Cantidad
                        {line.kind === "product" && line.presentationId
                          ? ` (en ${presentationsByProduct[line.productId]?.find((p) => p.id === line.presentationId)?.name ?? "presentación"})`
                          : line.kind === "ingredient" && ing
                          ? ` (${UNIT_SHORT[ing.unidadBase] ?? ""})`
                          : ""}
                      </label>
                      <Input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={line.qty}
                        onChange={(e) => updateLine(i, { qty: e.target.value })}
                        className="mt-0.5 h-9 text-right tabular-nums"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground">Costo unit.</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.costoUnitario}
                        onChange={(e) => updateLine(i, { costoUnitario: e.target.value })}
                        className="mt-0.5 h-9 text-right tabular-nums"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground">Lote (opc.)</label>
                      <Input
                        value={line.loteNumero}
                        onChange={(e) => updateLine(i, { loteNumero: e.target.value })}
                        className="mt-0.5 h-9"
                        placeholder="L-…"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground">Vence (opc.)</label>
                      <Input
                        type="date"
                        value={line.venceAt}
                        onChange={(e) => updateLine(i, { venceAt: e.target.value })}
                        className="mt-0.5 h-9"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-9 w-9 text-muted-foreground hover:text-red-400"
                        onClick={() => removeLine(i)}
                        disabled={lines.length <= 1}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    </div>
                  </div>
                );
              })}

              <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={addLine}>
                <Plus className="h-3.5 w-3.5" />
                Agregar línea
              </Button>
            </div>

            <div className="flex justify-end gap-2 border-t border-border/50 pt-3">
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button type="button" onClick={() => void handleCreate()} disabled={saving}>
                {saving ? "Guardando…" : "Confirmar recepción"}
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}

      {tab === "recepciones" && detailId ? (
        <Panel
          title={`Detalle · ${detailReceipt?.numero ?? detailId}`}
          description="Líneas ingresadas en esta recepción"
          actions={
            <button
              type="button"
              onClick={() => {
                setDetailId(null);
                setDetailLines([]);
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          }
        >
          {detailLoading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : detailLines.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin líneas</p>
          ) : (
            <ul className="space-y-1.5">
              {detailLines.map((line) => (
                <li
                  key={line.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2 text-sm"
                >
                  <span className="font-medium">{line.descripcion}</span>
                  <span className="tabular-nums text-muted-foreground">
                    +{line.qtyReceived} {UNIT_SHORT[line.unit] ?? line.unit}
                    {line.loteNumero ? ` · lote ${line.loteNumero}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : null}

      {tab === "recepciones" ? (
        <DataTable
          columns={columns}
          rows={receipts}
          getRowKey={(r) => r.id}
          emptyState={
            <CrudEmptyState
              title="No hay recepciones"
              description={
                receiptKind === "ingredient"
                  ? "Registrá la primera para cargar stock de insumos."
                  : "Registrá la primera para cargar stock de productos."
              }
            />
          }
        />
      ) : alerts.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10">
          <CheckCircle2 className="h-8 w-8 text-green-400" />
          <p className="text-sm text-muted-foreground">Sin alertas activas</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert) => {
            const nombre = typeof alert.payload?.nombre === "string" ? alert.payload.nombre : null;
            const stockActual = alert.payload?.stockActual;
            const umbral = alert.payload?.umbral;
            const isIngredientLow =
              alert.type === "low_stock" && alert.entityType === "ingredient" && !!alert.entityId;
            return (
              <div
                key={alert.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{ALERT_TYPE_LABELS[alert.type] ?? alert.type}</p>
                  <p className="text-xs text-muted-foreground">
                    {nombre ? (
                      <>
                        <span className="text-foreground">{nombre}</span>
                        {stockActual != null && umbral != null
                          ? ` · stock ${String(stockActual)} (umbral ${String(umbral)})`
                          : null}
                      </>
                    ) : (
                      new Date(alert.createdAt).toLocaleDateString("es-AR")
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isIngredientLow ? (
                    <>
                      <Button asChild size="sm" variant="outline" className="text-xs">
                        <Link
                          to="/app/compras"
                          search={{ tab: "recepciones", ingredientId: alert.entityId! }}
                        >
                          Recibir
                        </Link>
                      </Button>
                      <Button asChild size="sm" variant="outline" className="text-xs">
                        <Link to="/app/compras/ordenes" search={{ ingredientId: alert.entityId! } as never}>
                          Crear OC
                        </Link>
                      </Button>
                    </>
                  ) : null}
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => void handleAck(alert.id)}>
                    Reconocer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
